/**
 * LoginGrid — last 30 Israel days, one cell per day: green when any therapist
 * of the clinic had a telemetry session that day. Tooltip lists who.
 * Source: niv_telemetry_daily_therapist docs (getClinicTelemetryDetail.therapistDaily).
 */

import { useMemo } from "react";
import { SignIn } from "@phosphor-icons/react";
import HoverTip from "../../../../../components/ui/HoverTip";
import { formatDayKey } from "../../analytics/telemetryChartUtils";
import { lastNDayKeys } from "../clinicDetailConstants";
import { Block } from "./Skeletons";

const DAYS = 30;

export default function LoginGrid({ therapistDaily, isLoading }) {
  const { cells, activeDays } = useMemo(() => {
    const byDay = new Map();
    (therapistDaily || []).forEach((d) => {
      if (!d?.dateKey || !(Number(d.sessions) > 0)) return;
      const entry = byDay.get(d.dateKey) || { sessions: 0, names: new Set() };
      entry.sessions += Number(d.sessions) || 0;
      entry.names.add(d.therapistName || d.therapistId || "?");
      byDay.set(d.dateKey, entry);
    });
    const keys = lastNDayKeys(DAYS);
    const list = keys.map((dateKey) => {
      const e = byDay.get(dateKey);
      return {
        dateKey,
        active: !!e,
        sessions: e?.sessions || 0,
        names: e ? Array.from(e.names) : [],
      };
    });
    return { cells: list, activeDays: list.filter((c) => c.active).length };
  }, [therapistDaily]);

  return (
    <section className="ncd2-card ncd2-logins">
      <header className="ncd2-card-head">
        <span className="ncd2-card-icon">
          <SignIn size={15} weight="bold" />
        </span>
        <div className="ncd2-card-titles">
          <span className="ncd2-card-eyebrow">30 ימים אחרונים</span>
          <h3 className="ncd2-card-title">
            ימי כניסה
            {!isLoading && (
              <span className="ncd2-card-count">
                {activeDays}/{DAYS}
              </span>
            )}
          </h3>
        </div>
      </header>
      {isLoading ? (
        <Block w="100%" h={22} />
      ) : (
        <div className="ncd2-login-grid" role="img" aria-label={`${activeDays} ימי כניסה מתוך ${DAYS}`}>
          {cells.map((c, i) => (
            <HoverTip
              key={c.dateKey}
              text={
                c.active
                  ? `${formatDayKey(c.dateKey)} · ${c.sessions} כניסות · ${c.names.join(", ")}`
                  : `${formatDayKey(c.dateKey)} · אין כניסות`
              }
            >
              <span
                className={`ncd2-login-cell${c.active ? " is-active" : ""}`}
                style={{ animationDelay: `${i * 12}ms` }}
              />
            </HoverTip>
          ))}
        </div>
      )}
      <div className="ncd2-login-axis" aria-hidden="true">
        <span>{formatDayKey(cells[0]?.dateKey)}</span>
        <span>היום</span>
      </div>
    </section>
  );
}
